import { useEffect, useState, createContext, useContext, useMemo } from 'react';

import { useAuthContext } from 'src/auth/hooks';
import useKeyAssumptions, { ScenarioStack } from 'src/contexts/useKeyAssumptions';
import { useLocalStorage } from 'src/hooks/use-local-storage';
import brandColors from 'src/utils/brand-colors';
import useSegments from './segments';
import ForecastEngine from './ForecastEngine';
import { usePlaidDataSource } from './usePlaidDataSource';
import { getForecastingIndex, getRetirementIndex } from '../forecast/utils';
import useGameService from './use-game-service';
import sanitizeDateRange from './sanitizeDateRange';
import annotationsForGoals from '../chart/annotationsForGoals';
import pointsOfInterest from '../chart/points-of-interest';

const FinanceContext = createContext({});

const forecastEngine = new ForecastEngine();

const FinanceProvider = ({ children }) => {
  const { user } = useAuthContext();
  const userId = user?.id ?? user?.uid;

  // UI Segments & data source
  const {
    transactions,
    userAssets,
    debts,
    budgetSnapshots,
    accountsSnapshots,
    loading,
    accounts,
    fullRange: _fullRange,
    assetService,
    scenarioService,
    goalService,
    patches: allPatches,
    income: incomeData,
    expenses: expenseData,
    assetSnapshots,
    liabilitySnapshots,
    hasPendingTasks,
  } = usePlaidDataSource(userId);

  const fullRange = useMemo(() => sanitizeDateRange(_fullRange), [_fullRange]);

  // load key assumptions (calculation variables) for different scenarios
  const [scenario, setScenario] = useLocalStorage('scenario', 'default');

  const agg_real_estate_value = (userAssets ?? [])
    .filter((e) => e?.type === 'real_estate')
    .reduce((acc, e) => acc + (e?.value ?? 0), 0);

  const agg_private_asset_value = (userAssets ?? [])
    ?.filter((e) => e?.type === 'other')
    .reduce((acc, e) => acc + (e?.value ?? 0), 0);

  // stack the scenario key assumptions on top of the users 'base case'
  const { systemAssumptions, baseAssumptions, scenarioAssumptions, keyAssumptions } = useMemo(
    () =>
      new ScenarioStack(scenarioService.scenarios, scenario, {
        strategy: 'conservative',
        real_estate_value: agg_real_estate_value,
        private_asset_value: agg_private_asset_value,
      }).compileStack(),
    [scenarioService.scenarios, scenario]
  );

  const retirementInfo = {
    birthdate: user?.dob?.toDate(),
    targetRetirementAge: keyAssumptions?.targetRetirementAge,
  };

  const { period, setPeriod, dateRange, setDateRange, debouncedRange, segments } = useSegments(
    fullRange,
    retirementInfo
  );

  // toggle between actual (historical) values or budget (snapshot) values
  const [dataSource, setDataSource] = useState('actual');

  // get the patches for the current scenario & "global" budgets (budgets without a scenario id)
  const patches = allPatches?.filter((patch) => patch.scenario === scenario || !patch.scenario);

  // dynamic assumptions
  const { averageEquityGrowthRate, averageRealEstateGrowthRate, propertyValue, birthdate } =
    useKeyAssumptions(period, { keyAssumptions, dob: user?.dob });

  // fixed rate assumptions
  const {
    annualInflationRate,
    averageIncomeGrowthRate,
    targetRetirementAge,
    averagePrivateAssetGrowthRate,
    privateAssetsValue,
    socialSecurityIncome,
    taxRateRetention,
    averageRetirementEquityWithdrawalRate,
    averageEquityWithdrawalRate,
  } = useKeyAssumptions('month', { keyAssumptions, dob: user?.dob });

  const [forecast, setForecast] = useState({});

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await forecastEngine.runForecasts({
          fullRange,
          profile: user,
          expenseData,
          incomeData,
          period,
          debouncedRange,
          userAssets,
          debts,
          accountsSnapshots,
          budgetSnapshots,
          averageEquityGrowthRate,
          annualInflationRate,
          averageEquityWithdrawalRate,
          patches,
          accounts,
          averageRealEstateGrowthRate,
          propertyValue,
          averageIncomeGrowthRate,
          targetRetirementAge,
          averageRetirementEquityWithdrawalRate,
          socialSecurityIncome,
          birthdate,
          averagePrivateAssetGrowthRate,
          privateAssetsValue,
          taxRateRetention,
          assetSnapshots,
          liabilitySnapshots,
        });
        setForecast(data);

        const log = false;
        if (log) {
          console.log('Forecasting variables', {
            fullRange,
            profile: user,
            expenseData,
            incomeData,
            period,
            debouncedRange,
            userAssets,
            debts,
            accountsSnapshots,
            budgetSnapshots,
            averageEquityGrowthRate,
            annualInflationRate,
            averageEquityWithdrawalRate,
            patches,
            accounts,
            averageRealEstateGrowthRate,
            propertyValue,
            averageIncomeGrowthRate,
            targetRetirementAge,
            averageRetirementEquityWithdrawalRate,
            socialSecurityIncome,
            birthdate,
            averagePrivateAssetGrowthRate,
            privateAssetsValue,
            taxRateRetention,
            assetSnapshots,
            liabilitySnapshots,
          });
        }
      } catch (error) {
        console.warn('Forecast Failed', error);
      }
    }
    fetchData();
  }, [
    period,
    debouncedRange,
    allPatches,
    scenario,
    keyAssumptions,
    debts,
    userAssets,
    fullRange,
    budgetSnapshots,
    assetSnapshots,
    liabilitySnapshots,
  ]);

  const { income, expenses, liabilities, assets, cashflow } = forecast ?? {};

  // provide the current segment date & its index (useful in UI)
  const [currentSegmentIndex, currentSegment, retirementSegment] = useMemo(() => {
    const index = getForecastingIndex(expenses?.segments ?? segments);
    const rtidx = getRetirementIndex(segments);
    return [index, expenses?.segments?.[index], expenses?.segments?.[rtidx]];
  }, [expenses?.segments, period, dateRange]);

  // points of interest annotations for the charts (goals, retirement, loan payoffs, etc.)
  const chartAnnotations = useMemo(() => {
    const goals = annotationsForGoals(goalService?.goals, period, brandColors);
    const points = pointsOfInterest({
      currentSegment,
      retirementSegment,
      liabilities,
      segments,
      rollup: period,
    });

    return { goals, points, all: goals.concat(points) };
  }, [currentSegment, retirementSegment, goalService?.goals, period]);

  const accountViewModels = useMemo(
    () => [
      ...(assets?.allAssets ?? []),
      ...(liabilities?.allLiabilities?.map(({ account, ...rest }) => ({ ...(account ?? rest) })) ??
        []),
    ],
    [assets, liabilities]
  );

  const _value = {
    goalService,
    income,
    expenses,
    cashflow,
    rollup: period,
    setRollup: setPeriod,
    userAssets, // assets the user has added
    assets, // asset forecast
    dateRange,
    setDateRange,
    debouncedRange,
    transactions,
    loading,
    liabilities,
    segments,
    currentSegment,
    currentSegmentIndex,
    budgetSnapshots,
    dataSource,
    setDataSource,
    accountsSnapshots,
    accounts,
    assetService,
    scenarioService,
    systemAssumptions,
    baseAssumptions,
    scenarioAssumptions,
    keyAssumptions,
    scenario,
    setScenario,
    patches,
    userId,
    assetSnapshots,
    liabilitySnapshots,
    hasPendingTasks,
    retirementSegment,
    chartAnnotations,
    accountViewModels,
  };

  const game = useGameService(_value);

  // Memoize the context value to prevent unnecessary recreations
  const contextValue = useMemo(() => ({ ..._value, game }), [_value, game]);

  return <FinanceContext.Provider value={contextValue}>{children}</FinanceContext.Provider>;
};

const useFinanceContext = () => {
  const context = useContext(FinanceContext);
  if (!context) throw new Error('useFinanceContext must be used inside TransactionsProvider');
  return context;
};

export { FinanceProvider, useFinanceContext };
